// app/src/features/timeline/TimelineColumn.jsx
// The timeline column of the desktop shell, moved verbatim out of AppInner (app/src/App.jsx) by PKG-04
// module #1. `ctx` carries the app state it renders; the slots (header, hero, runStatus,
// viewMomentBlock, tsdChart, trackingMarks) are the other buckets' JSX that sits inside the column
// until their own modules land, and `children` are the overlays that follow the lane. `answerBox`
// is the answer box a closed question with no answer shows beside its outcomes ({state, checkNow,
// onCheckNow}), or null.
import { useMemo } from "react";
import { CloseButton, ModalShell } from "../../components/Panels.jsx";
import { ResolvedOutcomeLabel } from "../../components/timeline/ResolvedOutcomeMark.jsx";
import { TriggerLegend } from "../../components/TriggerNode.jsx";
import { Button as LaneButton } from "../../components/ui/Primitives.jsx";
import { C, isLightTheme } from "../../lib/colors.js";
import { RAIL_SECTIONS } from "../../lib/navRail.js";
import { surface, ui } from "../../lib/telemetry/instrument.js";
import { AXIS_H, LABEL_MIN_PPD, R_START, clientToLocalX } from "../../lib/timeline/geometry.js";
import { triggerDiameterFor } from "../../lib/timeline/triggerSize.js";
import { useLaneCtx } from "./useLaneCtx.js";
import { renderTimelineContent } from "./renderers/renderTimelineContent.jsx";
import { renderDateAxisContents } from "./renderers/renderDateAxisContents.jsx";
import { renderContractBody } from "./renderers/renderContractBody.jsx";
import { renderRow } from "./renderers/renderRow.jsx";

function histTodayLabel(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Today";
  const n = d.getDate();
  const suf = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${d.toLocaleDateString("en-US", { month: "long" })} ${n}${suf}, ${d.getFullYear()}`;
}

/** The timeline column: lane band, sticky labels, date axis, crosshair and the Question popup. */
export default function TimelineColumn({
  ctx,
  answerBox = null,
  header,
  hero,
  runStatus,
  viewMomentBlock,
  tsdChart,
  trackingMarks,
  children,
}) {
  const {
    active,
    animPhase,
    animRef,
    centerToday,
    containerRef,
    contractOpen,
    crossX,
    displayActive,
    dragRef,
    earlyResolved,
    evtScrollRef,
    forecastHeaderH,
    forecastTracking,
    forecastingPhase,
    formatCrossDate,
    genX,
    laneLeftEdge,
    onMD,
    onTsdHandleMD,
    openCustomEventPopover,
    ppd,
    resScreenX,
    resolvedMark,
    resumeQuestionClearance,
    runStatusOn,
    screenToDay,
    setAxisHoverX,
    setContainerRef,
    setContractOpen,
    showGenLine,
    showTodayLine,
    signpostsH,
    theme,
    themeRaw,
    timelineLabelTop,
    timelineToday,
    todayX,
    tourActive,
    tsdH,
    viewSnap,
    vw,
  } = ctx;

  // Reads component state (hov for the hover flag, redBlueTeam for coloring), so it must stay
  // inside the component. labelY is NOT included because the callers clamp it differently
  // (renderEvt clamps against LABEL_H via Math.min; renderPromotedEvt does not) — see each call
  // site.
  // A future trigger's pie grows with how far it would move the forecast, relative to
  // this forecast's most consequential trigger (lib/timeline/triggerSize.js).
  const sizeEvents = displayActive?.events;
  const sizeProbability =
    typeof displayActive?.contract?.probability === "number"
      ? displayActive.contract.probability
      : typeof displayActive?.serverPrediction === "number"
        ? displayActive.serverPrediction
        : null;
  const triggerDiameter = useMemo(
    () => triggerDiameterFor(sizeEvents, sizeProbability),
    [sizeEvents, sizeProbability],
  );
  const base = { ...ctx, answerBox, sizeEvents, sizeProbability, triggerDiameter };
  // renderRow is bound to this render's context so ForecastLane can call it with its own arguments.
  const laneCtx = useLaneCtx({ ...base, renderRow: (...args) => renderRow(rctx, ...args) });
  const rctx = { ...base, laneCtx };

  const onCenterToday = () => {
    resumeQuestionClearance();
    centerToday();
  };
  return (
    <div
      data-testid="forecast-timeline"
      {...surface("timeline", active?.id)}
      ref={setContainerRef}
      // axisHoverX only drives the Forecast Tracking date-row tick
      // brightening, so attach these pointer-frequency state setters ONLY
      // when the flag is on — otherwise the base timeline re-rendered on
      // every mousemove for ALL users (a regression for non-FT users).
      onMouseMove={
        forecastTracking
          ? (e) => {
              setAxisHoverX(clientToLocalX(e.currentTarget, e.clientX).x);
            }
          : undefined
      }
      onMouseLeave={forecastTracking ? () => setAxisHoverX(null) : undefined}
      // overflow:"clip" (NOT "hidden"): the side drawers (Forecast Log / Chat Console) are
      // position:absolute children here and slide off via translateX, so their off-screen state
      // overflows this box horizontally. "hidden" leaves the box a scroll container, so when a
      // closing panel carries focus the browser scrolls it into view and sets scrollLeft,
      // dragging every absolute child (and the timeline) sideways. "clip" establishes NO scroll
      // container (scrollLeft can't be set) while clipping identically, so the panels can't be
      // shifted.
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "clip",
        background: C.bg,
      }}
    >
      {header}
      {/* telemetry-gesture: drag pans the timeline (view navigation); a double click opens the add-custom-event popover, whose submit is forecast.branch */}
      <div
        ref={evtScrollRef}
        className="evt-scroll"
        {...surface("timeline.canvas", active?.id)}
        // eslint-disable-next-line react-hooks/refs -- intentional render-time read (verbatim from App.jsx): the attribute marks an in-flight zoom animation
        {...(animRef.current ? { "data-zoom-anim-active": "" } : {})}
        {...(animPhase ? { "data-forecast-anim-active": "" } : {})}
        style={{
          flex: 1,
          overflowX: "hidden",
          overflowY: "auto",
          // eslint-disable-next-line react-hooks/refs -- intentional drag-cursor read (verbatim from App.jsx; non-reactive cursor is fine)
          cursor: dragRef.current ? "grabbing" : "grab",
          userSelect: "none",
          position: "relative",
        }}
        onMouseDown={onMD}
        onDoubleClick={(e) => {
          if (!active) return;
          const cont = containerRef.current;
          if (!cont) return;
          const sx = clientToLocalX(cont, e.clientX).x;
          const dayNum = screenToDay(sx);
          const date = new Date(R_START.getTime() + dayNum * 86400000);
          if (isNaN(date.getTime())) return;
          openCustomEventPopover({
            screenX: e.clientX,
            screenY: e.clientY,
            date: date.toISOString().slice(0, 10),
            // dateTime keeps the click's own instant alongside the truncated day.
            dateTime: date.toISOString(),
          });
        }}
      >
        {hero}
        {runStatus}
        {renderTimelineContent(rctx)}
      </div>
      {/* Sticky labels — don't scroll with content */}
      {viewMomentBlock}
      {active && showTodayLine && todayX > -10 && todayX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: todayX + 6,
            top: 8 + timelineLabelTop,
            fontSize: 12,
            lineHeight: "14px",
            color: C.today,
            opacity: 0.85,
            letterSpacing: 0,
            whiteSpace: "nowrap",
            fontWeight: 500,
            zIndex: 15,
            pointerEvents: "none",
          }}
        >
          {/* Uses timelineToday, the same displayed-snapshot moment the line sits on, so
         the label and the line can never disagree during an async load. */}
          {viewSnap ? histTodayLabel(timelineToday.toISOString()) : "Today"}
        </div>
      )}
      {/* Only when the whole label clears the left panel. */}
      {showGenLine && genX - 90 >= laneLeftEdge && genX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: genX - 90,
            top: 8 + timelineLabelTop,
            width: 84,
            textAlign: "right",
            fontSize: 12,
            lineHeight: "14px",
            color: C.gray,
            letterSpacing: 0,
            whiteSpace: "nowrap",
            fontWeight: 500,
            zIndex: 15,
            pointerEvents: "none",
          }}
        >
          Generated
        </div>
      )}
      {/* The manual-update pill that sat under TODAY is now the header's "Update now"
     (ForecastHeader; gating in lib/forecast/headerActions.js updateNowState). */}
      {resolvedMark && ppd >= LABEL_MIN_PPD && (
        <ResolvedOutcomeLabel
          x={resolvedMark.x}
          vw={vw}
          text={resolvedMark.text}
          top={8 + timelineLabelTop}
          color={C.gold}
        />
      )}
      {!resolvedMark &&
        resScreenX !== null &&
        resScreenX > -10 &&
        resScreenX < vw + 10 &&
        ppd >= LABEL_MIN_PPD &&
        (() => {
          // The manual-update pill under TODAY moved to the header, so nothing sits there.
          const reforecastShown = false;
          const todayLabelRight = todayX + 48;
          const reforecastRight = todayX + 95;
          const flabelLeft = resScreenX + 6;
          const flabelTop =
            (flabelLeft >= todayLabelRight + 8
              ? 8
              : !reforecastShown || flabelLeft >= reforecastRight + 8
                ? 24
                : 34) + timelineLabelTop;
          return (
            <div
              key={`flabel-${active?.id || ""}`}
              style={{
                position: "absolute",
                left: flabelLeft,
                top: flabelTop,
                fontSize: 12,
                lineHeight: "14px",
                color: C.accent,
                letterSpacing: 0,
                whiteSpace: "nowrap",
                fontWeight: 500,
                zIndex: 15,
                pointerEvents: "none",
                transition: "top 0.2s ease",
                animation:
                  forecastingPhase === "awaiting" ? "simpleFadeIn 0.3s ease-out 1.0s both" : "none",
              }}
            >
              {earlyResolved ? "Scheduled to resolve" : "Resolves"}
            </div>
          );
        })()}
      {/* The wrapper deliberately has no lift or transform: the Signal Library drawer
     (fixed, z62) is a tall panel that covers the timeline when open and paints over
     this strip. */}
      <div
        style={{
          // The start page shows no timeline, so no date bar either.
          display: active ? undefined : "none",
          flexShrink: 0,
          position: "relative",
          zIndex: "auto",
          transform: "none",
          transition: "none",
        }}
      >
        {/* The button lives inside the wrapper, so it stays aligned with the axis bar
       without a transform of its own. */}
        {/* The lane's key (bottom left, clear of the left panel) and the
       re-frame control (bottom right), both just above the axis. */}
        {/* The key teaches the marks, so it shows only during the tour. */}
        {active && !runStatusOn && tourActive && (
          <TriggerLegend
            color={C.grayL}
            muted={C.gray}
            bg={C.future}
            text={C.gray}
            style={{
              position: "absolute",
              left: laneLeftEdge + 10,
              bottom: "calc(100% + 30px)",
              zIndex: 19,
              pointerEvents: "none",
              background: C.bg,
              padding: "3px 8px",
              borderRadius: 6,
            }}
          />
        )}
        <LaneButton
          {...ui("timeline.center-today")}
          variant="ghost"
          size="sm"
          onClick={onCenterToday}
          title="Center the timeline on today through the resolution date"
          style={{
            position: "absolute",
            right: 12,
            bottom: "calc(100% + 28px)",
            zIndex: 20,
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          Center
        </LaneButton>
        {/* The whole date bar is a drag handle for the Signal Library drawer —
      same behavior as the trapezoid resize grip (onTsdHandleMD): click +
      drag up/down to open/resize the drawer. The global onMM/onMU on the
      app container drive setTsdH, so attaching the mousedown here is all
      that's needed. (Reforecast rewind marks live on a separate hit band,
      so this doesn't capture their clicks.) */}
        <div
          {...surface("timeline.axis", active?.id)}
          {...ui("tsd.resize-handle")}
          onMouseDown={onTsdHandleMD}
          style={{
            height: AXIS_H,
            flexShrink: 0,
            // Dawn: line above the months matches the vertical major gridline darkness.
            borderTop: `1px solid ${isLightTheme(theme) ? "#b0b0b0a8" : C.border}`,
            overflow: "hidden",
            position: "relative",
            background: C.sectionBg,
            cursor: "ns-resize",
          }}
        >
          {renderDateAxisContents(rctx)}
        </div>

        <div
          className="tsd-handle"
          {...ui("tsd.resize-handle")}
          onMouseDown={onTsdHandleMD}
          style={{
            height: 4,
            flexShrink: 0,
            background: C.sectionBg,
            borderTop: `1px solid ${C.border}`,
            borderBottom: `1px solid ${C.border}`,
            cursor: "ns-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "visible",
            position: "relative",
          }}
        >
          {/* SVG height 4 matches the handle's 4px content between its top/bottom border
         lines. */}
          <svg
            width="60"
            height="4"
            viewBox="0 0 60 4"
            style={{ opacity: 0.7, overflow: "visible", display: "block" }}
          >
            <path
              d="M 6,0 L 54,0 L 60,4 L 0,4 Z"
              fill={theme === "dusk" ? themeRaw.accent : themeRaw.action}
              stroke={theme === "dusk" ? themeRaw.accent : themeRaw.action}
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {tsdChart}
      {crossX !== null && (
        <>
          <div
            style={{
              position: "absolute",
              left: crossX,
              top: forecastHeaderH,
              // Stops at the signal bar.
              bottom: signpostsH,
              width: 0,
              borderLeft: "1px dashed #33556677",
              pointerEvents: "none",
              zIndex: 15,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: crossX + 6,
              bottom: tsdH + 6 + AXIS_H + 4 + signpostsH,
              fontSize: 12,
              color: C.grayL,
              fontFamily: '"IBM Plex Mono",monospace',
              fontVariantNumeric: "tabular-nums",
              pointerEvents: "none",
              zIndex: 16,
              whiteSpace: "nowrap",
              letterSpacing: 0,
            }}
          >
            {formatCrossDate(crossX)}
          </div>
        </>
      )}
      {trackingMarks}
      {contractOpen &&
        active && (
          // The header's "Resolution Criteria" opens this popup; the rail's panels stay as they are.
          <ModalShell
            onClose={() => setContractOpen(false)}
            surface="question"
            surfaceKey={active.id}
            panelProps={{
              "data-testid": "rail-question",
              role: "dialog",
              "aria-modal": "true",
              "aria-label": RAIL_SECTIONS[2].heading,
            }}
            panelStyle={{
              width: "min(640px, 92vw)",
              maxHeight: "85vh",
              overflowY: "auto",
              marginTop: "8vh",
            }}
          >
            <div style={{ padding: "20px 24px 4px", display: "flex", alignItems: "center" }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: C.white,
                  letterSpacing: 0,
                }}
              >
                {RAIL_SECTIONS[2].heading}
              </h2>
              <div style={{ flex: 1 }} />
              <CloseButton
                dataPopupCancel
                onClick={() => setContractOpen(false)}
                style={{ width: 32, height: 32, fontSize: 18, color: C.gray }}
              />
            </div>
            <div style={{ position: "relative" }}>{renderContractBody(rctx, true)}</div>
          </ModalShell>
        )}
      {children}
    </div>
  );
}
